<template>
  <div class="rep">
    <div class="rep__inner">
      <div class="rep__head">
        <div>
          <h1 class="rep__title">Izvještaji</h1>
          <div class="rep__sub">
            {{ periodLabel }} · {{ cohortLabel }}
            <template v-if="state.loading"> · <i class="el-icon-loading"></i> učitavanje…</template>
          </div>
        </div>
      </div>

      <section class="card rep__filters">
        <div class="presets">
          <button
            v-for="p in presets"
            :key="p.key"
            class="preset"
            :class="{ 'is-active': activePreset === p.key }"
            @click="applyPreset(p)"
          >{{ p.label }}</button>
        </div>

        <div class="filters-row">
          <div class="field">
            <label>Od</label>
            <el-date-picker v-model="formData.start" type="date" placeholder="Od" value-format="yyyy-M-d" size="small" @change="activePreset = ''" />
          </div>
          <div class="field">
            <label>Do</label>
            <el-date-picker v-model="formData.end" type="date" placeholder="Do" value-format="yyyy-M-d" size="small" @change="activePreset = ''" />
          </div>
          <div class="field">
            <label>Vrsta osiguranja</label>
            <el-select v-model="formData.vrsta_osiguranja" size="small" clearable filterable placeholder="Sve">
              <el-option v-for="i in formContent.vrsta_osiguranja" :key="i.value" :label="i.label" :value="i.value" />
            </el-select>
          </div>
          <div class="field">
            <label>Obaveza</label>
            <el-select v-model="formData.obaveza" size="small" multiple filterable collapse-tags placeholder="Sve">
              <el-option v-for="i in formContent.obaveza" :key="i.value" :label="i.label" :value="i.value" />
            </el-select>
          </div>
          <div class="field">
            <label>Zadužio</label>
            <el-select v-model="formData.uposleni" size="small" clearable filterable placeholder="Svi">
              <el-option v-for="i in formContent.users" :key="i.value" :label="i.label" :value="i.value" />
            </el-select>
          </div>
          <div class="field">
            <label>Saradnik</label>
            <el-select v-model="formData.saradnik" size="small" clearable filterable placeholder="Svi">
              <el-option v-for="i in formContent.saradnik" :key="i.value" :label="i.label" :value="i.value" />
            </el-select>
          </div>
          <div class="field">
            <label>Faza</label>
            <el-select v-model="formData.status" size="small" clearable filterable placeholder="Sve">
              <el-option v-for="i in formContent.status" :key="i.value" :label="i.label" :value="i.value" />
            </el-select>
          </div>
          <div class="field">
            <label>Nosioc</label>
            <el-select v-model="formData.nosioc" size="small" clearable placeholder="Svi">
              <el-option v-for="n in nosioci" :key="n.id" :label="n.naziv" :value="n.id" />
            </el-select>
          </div>
          <div class="field field--check">
            <el-checkbox v-model="formData.otkup_ponuda">Samo s ponuđenim otkupom</el-checkbox>
          </div>
          <div class="field field--check">
            <el-checkbox v-model="formData.closed">Uključi zatvorene u periodu</el-checkbox>
          </div>
          <el-button type="primary" size="small" :loading="state.loading" @click="generateReport">
            Primijeni
          </el-button>
        </div>
      </section>

      <div class="kpis">
        <div class="kpi">
          <span class="kpi__label">Predmeti</span>
          <span class="kpi__value">{{ fmtInt(state.report.summary.total) }}</span>
          <span class="kpi__meta">{{ fmtInt(activeCount) }} aktivnih · {{ fmtInt(state.report.summary.total_done) }} završenih</span>
        </div>
        <div class="kpi">
          <span class="kpi__label">Naplaćeno</span>
          <span class="kpi__value">{{ fmtMoney(fin.total.total.income) }}</span>
          <span class="kpi__meta">na predmetima iz perioda, kad god je uplaćeno</span>
        </div>
        <div class="kpi">
          <span class="kpi__label">Isplaćeno</span>
          <span class="kpi__value">{{ fmtMoney(fin.total.total.cost) }}</span>
          <span class="kpi__meta">klijentima, otkupi i avansi</span>
        </div>
        <div class="kpi kpi--accent">
          <span class="kpi__label">Profit</span>
          <span class="kpi__value" :class="{ 'kpi__value--neg': (fin.total.total.profit || 0) < 0 }">
            {{ fmtMoney(fin.total.total.profit) }}
          </span>
          <span class="kpi__meta">naplaćeno − isplaćeno</span>
        </div>
        <div class="kpi">
          <span class="kpi__label">Potraživanje</span>
          <span class="kpi__value">{{ fmtMoney(fin.total.total.ask) }}</span>
          <span class="kpi__meta">isplaćeno, još nenaplaćeno (otvoreni predmeti)</span>
        </div>
      </div>

      <ChartCard
        title="Predmeti kroz vrijeme"
        subtitle="predmeti iz perioda; „nakon perioda” su oni zatvoreni kasnije"
        type="bar"
        :data="casesOverTime"
        :height="250"
        comparable
        :comparing="compare.cases"
        :compare-hint="prevLabel"
        :loading="compareLoading"
        @toggle="toggleCompare('cases')"
      />

      <ChartCard
        title="Novac kroz vrijeme"
        subtitle="sav novac na predmetima iz perioda, po datumu svake stavke — zbir je jednak karticama gore"
        type="bar"
        :data="moneyOverTime"
        :options="moneyOptions"
        :height="250"
        money
        comparable
        :comparing="compare.money"
        :compare-hint="prevLabel"
        :loading="compareLoading"
        @toggle="toggleCompare('money')"
      />

      <div class="grid2">
        <ChartCard
          title="Po fazi"
          type="bar"
          :data="byPhase"
          :options="phaseOptions"
          :height="270"
          comparable
          :comparing="compare.phase"
          :compare-hint="prevLabel"
          :loading="compareLoading"
          @toggle="toggleCompare('phase')"
        />
        <section class="card">
          <header class="card__head">
            <h2 class="card__title">Po vrsti osiguranja</h2>
            <span class="card__hint">udio u ukupnom broju</span>
          </header>
          <div v-if="!typeShare.length" class="share__empty">Nema podataka za odabrani period</div>
          <div v-else class="share">
            <div v-for="r in typeShare" :key="r.label" class="share__row">
              <span class="share__label" :title="r.label">{{ r.label }}</span>
              <span class="share__track"><span class="share__fill" :style="{ width: Math.max(r.pct, 0.6) + '%' }"></span></span>
              <span class="share__count">{{ fmtInt(r.value) }}</span>
              <span class="share__pct">{{ r.pct.toFixed(1) }}%</span>
            </div>
          </div>
        </section>
      </div>

      <ChartCard
        title="Vrijeme do zatvaranja po obavezniku"
        subtitle="prosječan broj dana od prijema do zatvaranja, po mjesecu zatvaranja; predmeti zatvoreni poslije perioda su u „nakon perioda”"
        type="line"
        :data="closeTime"
        :options="closeTimeOptions"
        :height="280"
        comparable
        :comparing="compare.closeTime"
        :compare-hint="prevLabel"
        :loading="compareLoading"
        @toggle="toggleCompare('closeTime')"
      />

      <ChartCard
        title="Top obaveze"
        subtitle="10 obaveza s najviše predmeta"
        type="bar"
        :data="byObaveza"
        :options="obavezaOptions"
        :height="300"
        comparable
        :comparing="compare.obaveza"
        :compare-hint="prevLabel"
        :loading="compareLoading"
        @toggle="toggleCompare('obaveza')"
      />

      <section class="card">
        <header class="card__head">
          <h2 class="card__title">Otkup</h2>
          <span class="card__hint">predmeti u kojima je otkup stvarno plaćen</span>
        </header>
        <div class="otkup-kpis">
          <div class="okpi">
            <span class="okpi__label">Otkupljeno predmeta</span>
            <span class="okpi__value">{{ fmtInt(otkup.summary.broj) }}</span>
            <span class="okpi__meta">{{ fmtInt(otkup.summary.otvoreno_broj) }} još otvoreno</span>
          </div>
          <div class="okpi">
            <span class="okpi__label">Uloženo</span>
            <span class="okpi__value">{{ fmtMoney(otkup.summary.ulozeno) }}</span>
            <span class="okpi__meta">isplaćeno za otkupe</span>
          </div>
          <div class="okpi">
            <span class="okpi__label">Isplaćeno</span>
            <span class="okpi__value">{{ fmtMoney(otkup.summary.isplaceno) }}</span>
            <span class="okpi__meta">ukupno na tim predmetima</span>
          </div>
          <div class="okpi">
            <span class="okpi__label">Naplaćeno</span>
            <span class="okpi__value">{{ fmtMoney(otkup.summary.naplaceno) }}</span>
            <span class="okpi__meta">na tim predmetima</span>
          </div>
          <div class="okpi okpi--accent">
            <span class="okpi__label">Prinos</span>
            <span class="okpi__value" :class="{ 'okpi__value--neg': (otkup.summary.prinos || 0) < 0 }">
              {{ otkup.summary.prinos === null ? "—" : (otkup.summary.prinos > 0 ? "+" : "") + otkup.summary.prinos + "%" }}
            </span>
            <span class="okpi__meta">razlika {{ fmtMoney(otkup.summary.razlika) }} KM</span>
          </div>
          <div class="okpi">
            <span class="okpi__label">Prosječni udio</span>
            <span class="okpi__value">{{ otkup.summary.prosjecni_udio === null ? "—" : otkup.summary.prosjecni_udio + "%" }}</span>
            <span class="okpi__meta">koliko plaćamo od zahtjeva</span>
          </div>
          <div class="okpi">
            <span class="okpi__label">Otvorena izloženost</span>
            <span class="okpi__value">{{ fmtMoney(otkup.summary.otvorena_izlozenost) }}</span>
            <span class="okpi__meta">uloženo, još nenaplaćeno</span>
          </div>
        </div>
      </section>

      <ChartCard
        title="Otkup kroz vrijeme"
        subtitle="po datumu svake stavke; zbir je jednak karticama Otkup gore"
        type="bar"
        :data="otkupOverTime"
        :height="260"
        money
        comparable
        :comparing="compare.otkup"
        :compare-hint="prevLabel"
        :loading="compareLoading"
        @toggle="toggleCompare('otkup')"
      />

      <div>
        <section class="card">
          <header class="card__head">
            <h2 class="card__title">Otkup po obavezniku</h2>
            <span class="card__hint">broj otkupa, uloženo i naplaćeno</span>
          </header>
          <div v-if="!otkup.po_obavezi.length" class="share__empty">Nema otkupa u odabranom periodu</div>
          <div v-else class="otbl-wrap">
            <table class="otbl">
              <thead>
                <tr>
                  <th class="otbl__name">Obveznik</th>
                  <th class="otbl__num">Otkupa</th>
                  <th class="otbl__num">Uloženo</th>
                  <th class="otbl__num">Naplaćeno</th>
                  <th class="otbl__num">Razlika</th>
                  <th class="otbl__num">Prinos</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in otkupPageRows" :key="r.obaveza">
                  <td class="otbl__name" :title="r.naziv">{{ r.naziv }}</td>
                  <td class="otbl__num">{{ fmtInt(r.broj) }}</td>
                  <td class="otbl__num">{{ fmtMoney(r.ulozeno) }}</td>
                  <td class="otbl__num">{{ fmtMoney(r.naplaceno) }}</td>
                  <td class="otbl__num" :class="{ 'otbl__neg': r.razlika < 0 }">{{ fmtMoney(r.razlika) }}</td>
                  <td class="otbl__num otbl__prinos" :class="{ 'otbl__neg': (r.prinos || 0) < 0 }">
                    {{ r.prinos === null ? "—" : (r.prinos > 0 ? "+" : "") + r.prinos + "%" }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="otkupRows.length > otkupPageSize" class="otbl__foot">
            <span class="otbl__count">
              {{ (otkupPage - 1) * otkupPageSize + 1 }}–{{
                Math.min(otkupPage * otkupPageSize, otkupRows.length)
              }}
              od {{ otkupRows.length }} obveznika
            </span>
            <el-pagination
              background
              layout="prev, pager, next"
              :page-size="otkupPageSize"
              :total="otkupRows.length"
              :current-page.sync="otkupPage"
            />
          </div>
        </section>
      </div>

      <section class="card">
        <header class="card__head">
          <h2 class="card__title">Finansijski pregled</h2>
          <span class="card__hint">
            iznosi u KM · „bez realizacije” = zatvoreni s razlogom koji nije „Završen”
            (neosnovano, odustali, drugo)
          </span>
        </header>
        <div class="fin-table-wrap">
          <table class="fin-table">
            <thead>
              <tr>
                <th></th>
                <th v-for="c in finCols" :key="c.key">{{ c.label }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in finRows" :key="row.key">
                <th scope="row">{{ row.label }}</th>
                <td v-for="c in finCols" :key="c.key">
                  <template v-if="row.key === 'count'">
                    {{ fmtInt(cell(c, "count")) }}
                    <span v-if="cell(c, 'count_lost')" class="fin-table__sub">
                      {{ fmtInt(cell(c, "count_lost")) }} bez realizacije
                    </span>
                  </template>
                  <template v-else>{{ fmtMoney(cell(c, row.key)) }}</template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="card">
        <header class="card__head">
          <h2 class="card__title">Predmeti</h2>
          <span class="card__hint">
            {{ formData.otkup_ponuda ? "samo predmeti s ponuđenim otkupom" : "svi predmeti u odabranom periodu" }}
          </span>
        </header>
        <div v-if="!caseRows.length" class="share__empty">Nema predmeta za odabrani period</div>
        <template v-else>
          <div class="ptbl-wrap">
            <table class="ptbl">
              <thead>
                <tr>
                  <th rowspan="2" class="ptbl__eye"></th>
                  <th rowspan="2" class="ptbl__sort" @click="sortCases('broj')">
                    Broj <span class="ptbl__arrow">{{ sortArrow("broj") }}</span>
                  </th>
                  <th rowspan="2" class="ptbl__sort" @click="sortCases('datum_prijema')">
                    Datum <span class="ptbl__arrow">{{ sortArrow("datum_prijema") }}</span>
                  </th>
                  <th rowspan="2" class="ptbl__sort" @click="sortCases('datum_zatvaranja')">
                    Zatvoreno na <span class="ptbl__arrow">{{ sortArrow("datum_zatvaranja") }}</span>
                  </th>
                  <th rowspan="2">Status</th>
                  <th rowspan="2">Vrsta</th>
                  <th rowspan="2">Uposleni</th>
                  <th rowspan="2">Nosioc</th>
                  <th colspan="4" class="ptbl__group">Finansije</th>
                  <th rowspan="2">Otkup ponuđen</th>
                  <th rowspan="2">Otkup prihvaćen</th>
                </tr>
                <tr>
                  <th class="ptbl__num">Naplaćeno</th>
                  <th class="ptbl__num">Plaćeno</th>
                  <th class="ptbl__num">Potraživanje</th>
                  <th class="ptbl__num">Profit</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in casePageRows" :key="r.id">
                  <td class="ptbl__eye">
                    <i
                      class="el-icon-view ptbl__open"
                      title="Otvori predmet"
                      @click="$router.push('/predmet/view/' + r.id)"
                    ></i>
                  </td>
                  <td class="ptbl__broj">{{ r.broj }}</td>
                  <td>{{ r.datum }}</td>
                  <td>{{ r.zatvoreno }}</td>
                  <td><StatusTag :value="r.status" /></td>
                  <td :title="r.vrsta">{{ r.vrsta }}</td>
                  <td :title="r.uposleni">{{ r.uposleni }}</td>
                  <td :title="r.nosioc">{{ r.nosioc }}</td>
                  <td class="ptbl__num">{{ fmtMoney(r.naplaceno) }}</td>
                  <td class="ptbl__num">{{ fmtMoney(r.placeno) }}</td>
                  <td class="ptbl__num">{{ fmtMoney(r.potrazivanje) }}</td>
                  <td class="ptbl__num" :class="{ 'ptbl__neg': r.profit < 0 }">{{ fmtMoney(r.profit) }}</td>
                  <td :class="r.otkup_ponuden ? 'ptbl__yes' : 'ptbl__no'">
                    <template v-if="r.otkup_ponuden">DA ({{ fmtMoney(r.otkup_ponuda) }})</template>
                    <template v-else>NE</template>
                  </td>
                  <td :class="r.otkup_prihvacen ? 'ptbl__yes' : 'ptbl__no'">
                    <template v-if="r.otkup_prihvacen">DA ({{ fmtMoney(r.otkup_placeno) }})</template>
                    <template v-else>NE</template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="otbl__foot">
            <span class="otbl__count">
              {{ (casePage - 1) * casePageSize + 1 }}–{{
                Math.min(casePage * casePageSize, caseRows.length)
              }}
              od {{ fmtInt(caseRows.length) }} predmeta
            </span>
            <el-pagination
              background
              layout="prev, pager, next"
              :page-size="casePageSize"
              :total="caseRows.length"
              :current-page.sync="casePage"
            />
          </div>
        </template>
      </section>
    </div>
  </div>
</template>

<script>
import API from "@/services/api/api";
import moment from "moment";
import ChartCard from "@/components/charts/ChartCard";
import { loadNosioci } from "@/models/nosioc";
import StatusTag from "@/components/StatusTag";
import { STATUSES, getStatusLabel, selectablePhases } from "@/models/status";

export default {
  name: "Reporting",
  components: { ChartCard, StatusTag },
  data() {
    return {
      activePreset: "today",
      nosioci: [],
      otkupPage: 1,
      otkupPageSize: 10,
      casePage: 1,
      casePageSize: 25,
      caseSort: { field: "broj", dir: "asc" },
      // Each chart decides for itself whether to draw the previous
      // period; the data behind it is fetched once and shared.
      compare: { cases: false, money: false, phase: false, closeTime: false, obaveza: false, otkup: false },
      compareReport: null,
      compareSig: null,
      compareLoading: false,
      TINT: { a: "#9ab6f6", b: "#edb791", neutral: "#94a3b8" },
      SERIES: { a: "#2f6fed", b: "#d97742" },
      presets: [
        { key: "today", label: "Danas" },
        { key: "month", label: "Ovaj mjesec" },
        { key: "ytd", label: "Ova godina" },
        { key: "last12", label: "Zadnjih 12 mjeseci" },
        { key: "prev", label: "Prošla godina" },
        { key: "q", label: "Ovaj kvartal" },
        { key: "all", label: "Sve" },
      ],
      finCols: [
        { key: "total.total", label: "Ukupno" },
        { key: "total.active", label: "Aktivno" },
        { key: "total.done", label: "Završeno" },
        { key: "otkup_ponuden.total", label: "Otkup ponuđen" },
        { key: "otkupljeno.total", label: "Otkupljeno" },
        { key: "otkupljeno.active", label: "Otkup aktivno" },
        { key: "otkupljeno.done", label: "Otkup završeno" },
      ],
      finRows: [
        { key: "count", label: "Broj predmeta" },
        { key: "cost", label: "Isplaćeno" },
        { key: "income", label: "Naplaćeno" },
        { key: "ask", label: "Potraživanje" },
        { key: "profit", label: "Profit" },
      ],
      state: {
        loading: false,
        report: {
          summary: {
            total: 0,
            total_done: 0,
            type: [],
            phase: [],
          },
          financial: {
            total: {
              total: {},
              active: {},
              done: {},
            },
            otkupljeno: {
              total: {},
              active: {},
              done: {},
            },
            sporazum: {
              total: {},
              active: {},
              done: {},
            },
          },
        },
      },
      formData: {
        start: String(new Date().getFullYear()) + "-1-1",
        end: String(new Date().getFullYear()) + "-12-31",
        closed: false,
        otkup_ponuda: false,
        // saradnik: 0,
        // vrsta_osiguranja: 0,
        obaveza: [],
        // special_filter: 0,
      },
      formContent: {
        vrsta_osiguranja: [],
        saradnik: [],
        obaveza: [],
        special_filter: [],
        status: [],
        users: [],
      },
    };
  },
  computed: {
    fin() {
      return this.state.report.financial;
    },
    activeCount() {
      return (this.state.report.summary.total || 0) - (this.state.report.summary.total_done || 0);
    },
    periodLabel() {
      const f = (d) => (d ? moment(d, "YYYY-M-D").format("DD.MM.YYYY.") : "");
      return `${f(this.formData.start)} – ${f(this.formData.end)}`;
    },
    timeline() {
      return this.state.report.timeline || [];
    },
    // The equal-length window immediately before the selected one, so any
    // custom range can be compared and not just whole months or years.
    prevRange() {
      const f = "YYYY-M-D";
      const start = moment(this.formData.start, f);
      const end = moment(this.formData.end, f);
      if (!start.isValid() || !end.isValid() || end.isBefore(start)) return null;
      // A whole calendar year or month compares against the whole previous
      // one. Measuring equal lengths instead would start the 2024 comparison
      // on 2 January, because a leap year is a day longer.
      const wholeYear =
        start.isSame(start.clone().startOf("year"), "day") &&
        end.isSame(end.clone().endOf("year"), "day") &&
        start.isSame(end, "year");
      if (wholeYear) {
        const prev = start.clone().subtract(1, "year");
        return {
          start: prev.clone().startOf("year").format(f),
          end: prev.clone().endOf("year").format(f),
        };
      }
      const wholeMonth =
        start.isSame(start.clone().startOf("month"), "day") &&
        end.isSame(end.clone().endOf("month"), "day") &&
        start.isSame(end, "month");
      if (wholeMonth) {
        const prev = start.clone().subtract(1, "month");
        return {
          start: prev.clone().startOf("month").format(f),
          end: prev.clone().endOf("month").format(f),
        };
      }
      const days = end.diff(start, "days") + 1;
      const prevEnd = start.clone().subtract(1, "day");
      return {
        start: prevEnd.clone().subtract(days - 1, "days").format(f),
        end: prevEnd.format(f),
      };
    },
    // The page is about a set of cases, not about cash that moved in the
    // window, and the difference is large enough to state outright.
    cohortLabel() {
      return this.formData.closed
        ? "predmeti otvoreni u periodu i predmeti zatvoreni u periodu"
        : "predmeti otvoreni u periodu";
    },
    prevLabel() {
      const r = this.prevRange;
      if (!r) return "";
      const f = "YYYY-M-D";
      return (
        moment(r.start, f).format("DD.MM.YYYY.") + " – " + moment(r.end, f).format("DD.MM.YYYY.")
      );
    },
    compareOn() {
      return Object.keys(this.compare).some((k) => this.compare[k]);
    },
    cmpTimeline() {
      return (this.compareReport && this.compareReport.timeline) || [];
    },
    monthLabels() {
      return this.timeline.map((t) => this.bucketLabel(t.month));
    },
    casesOverTime() {
      return {
        labels: this.monthLabels,
        datasets: [
          {
            label: "Primljeni",
            data: this.timeline.map((t) => t.primljeno),
            backgroundColor: this.SERIES.a,
            borderRadius: 3,
            maxBarThickness: this.compare.cases ? 12 : 22,
          },
          ...(this.compare.cases
            ? [this.cmpBar("Primljeni", this.alignCmp(this.timeline, this.cmpTimeline, (t) => t.primljeno), this.TINT.a)]
            : []),
          {
            label: "Zatvoreni",
            data: this.timeline.map((t) => t.zatvoreno),
            backgroundColor: this.SERIES.b,
            borderRadius: 3,
            maxBarThickness: this.compare.cases ? 12 : 22,
          },
          ...(this.compare.cases
            ? [this.cmpBar("Zatvoreni", this.alignCmp(this.timeline, this.cmpTimeline, (t) => t.zatvoreno), this.TINT.b)]
            : []),
        ],
      };
    },
    moneyOverTime() {
      return {
        labels: this.monthLabels,
        datasets: [
          {
            label: "Naplaćeno",
            data: this.timeline.map((t) => Math.round(t.naplaceno)),
            backgroundColor: this.SERIES.a,
            borderRadius: 3,
            maxBarThickness: this.compare.money ? 12 : 22,
          },
          ...(this.compare.money
            ? [this.cmpBar("Naplaćeno", this.alignCmp(this.timeline, this.cmpTimeline, (t) => Math.round(t.naplaceno)), this.TINT.a)]
            : []),
          {
            label: "Isplaćeno",
            data: this.timeline.map((t) => Math.round(t.isplaceno)),
            backgroundColor: this.SERIES.b,
            borderRadius: 3,
            maxBarThickness: this.compare.money ? 12 : 22,
          },
          ...(this.compare.money
            ? [this.cmpBar("Isplaćeno", this.alignCmp(this.timeline, this.cmpTimeline, (t) => Math.round(t.isplaceno)), this.TINT.b)]
            : []),
          {
            type: "line",
            label: "Profit",
            data: this.timeline.map((t) => Math.round(t.naplaceno - t.isplaceno)),
            borderColor: "#17222e",
            backgroundColor: "#17222e",
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 4,
            tension: 0.25,
          },
          ...(this.compare.money
            ? [this.cmpLine("Profit", this.alignCmp(this.timeline, this.cmpTimeline, (t) => Math.round(t.naplaceno - t.isplaceno)), this.TINT.neutral)]
            : []),
        ],
      };
    },
    moneyOptions() {
      return { scales: { y: { beginAtZero: true } } };
    },
    phaseOptions() {
      return { ...this.horizontalOptions, plugins: { legend: { display: this.compare.phase } } };
    },
    obavezaOptions() {
      return { ...this.horizontalOptions, plugins: { legend: { display: this.compare.obaveza } } };
    },
    horizontalOptions() {
      return {
        indexAxis: "y",
        plugins: { legend: { display: false } },
        // every category keeps its label — skipping them leaves bars that
        // cannot be identified
        scales: { y: { ticks: { autoSkip: false } } },
      };
    },
    // Phases keep the colours they carry everywhere else in the app.
    byPhase() {
      const counts = this.state.report.summary.phase.total || [];
      const rows = STATUSES.filter((st) => !st.historyOnly)
        .map((st) => {
          // the API returns these ids as strings ("0", "10")
          const hit = counts.find((c) => c.ids && parseInt(c.ids.status, 10) === st.id);
          return { id: st.id, label: st.label, value: hit ? hit.count : 0, color: st.dot };
        })
        .filter((r) => r.value > 0);
      const datasets = [
        {
          label: "Odabrani period",
          data: rows.map((r) => r.value),
          backgroundColor: rows.map((r) => r.color),
          borderRadius: 3,
          maxBarThickness: 20,
        },
      ];
      if (this.compare.phase) {
        const prev = (this.compareReport && this.compareReport.summary.phase.total) || [];
        datasets.push({
          label: "Prethodni period",
          data: rows.map((r) => {
            const hit = prev.find((c) => c.ids && parseInt(c.ids.status, 10) === r.id);
            return hit ? hit.count : 0;
          }),
          backgroundColor: this.TINT.neutral,
          borderRadius: 3,
          maxBarThickness: 20,
        });
      }
      return { labels: rows.map((r) => r.label), datasets };
    },
    // One line per insurer that actually closes enough cases to have a trend.
    closeTime() {
      const ct = this.state.report.close_time || { rows: [], volume: [] };
      const months = Array.from(new Set(ct.rows.map((r) => r.month))).sort();
      const top = (ct.volume || []).filter((v) => v.obaveza !== null).slice(0, 5);
      const palette = ["#2f6fed", "#d97742", "#46b08c", "#7c6fd0", "#c9a227"];
      const datasets = top.map((v, i) => {
        const byMonth = {};
        ct.rows.forEach((r) => {
          if (r.obaveza === v.obaveza) byMonth[r.month] = r.avg_days;
        });
        return {
          label: this.cleanName(this.getLabel("obaveza", v.obaveza)),
          data: months.map((m) => (byMonth[m] === undefined ? null : byMonth[m])),
          borderColor: palette[i],
          backgroundColor: palette[i],
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0.3,
          spanGaps: true,
        };
      });
      if (this.compare.closeTime) {
        const pct = (this.compareReport && this.compareReport.close_time) || { rows: [] };
        const pMonths = Array.from(new Set(pct.rows.map((r) => r.month))).sort();
        top.forEach((v, i) => {
          const byMonth = {};
          pct.rows.forEach((r) => {
            if (r.obaveza === v.obaveza) byMonth[r.month] = r.avg_days;
          });
          datasets.push({
            label: this.cleanName(this.getLabel("obaveza", v.obaveza)) + " (prethodni)",
            // aligned by position: the periods are equal in length, so the
            // n-th month of one lines up with the n-th month of the other
            data: months.map((_, idx) => {
              const m = pMonths[idx];
              return m === undefined || byMonth[m] === undefined ? null : byMonth[m];
            }),
            borderColor: palette[i],
            backgroundColor: palette[i],
            borderWidth: 2,
            borderDash: [5, 4],
            pointRadius: 0,
            pointHoverRadius: 4,
            tension: 0.3,
            spanGaps: true,
          });
        });
      }
      return {
        labels: months.map((m) => this.bucketLabel(m)),
        datasets,
      };
    },
    closeTimeOptions() {
      return {
        plugins: { legend: { display: true } },
        scales: { y: { title: { display: true, text: "dana" } } },
      };
    },
    otkup() {
      return (
        this.state.report.otkup || {
          summary: { broj: 0, ulozeno: 0, isplaceno: 0, naplaceno: 0, razlika: 0, prinos: null, otvoreno_broj: 0, otvorena_izlozenost: 0, prosjecni_udio: null },
          mjesecno: [],
          po_obavezi: [],
        }
      );
    },
    otkupOverTime() {
      const m = this.otkup.mjesecno || [];
      const cmp = (this.compareReport && this.compareReport.otkup && this.compareReport.otkup.mjesecno) || [];
      return {
        labels: m.map((x) => this.bucketLabel(x.month)),
        datasets: [
          {
            label: "Uloženo",
            data: m.map((x) => Math.round(x.ulozeno)),
            backgroundColor: "#d97742",
            borderRadius: 3,
            maxBarThickness: this.compare.otkup ? 12 : 22,
          },
          ...(this.compare.otkup
            ? [this.cmpBar("Uloženo", this.alignCmp(m, cmp, (x) => Math.round(x.ulozeno)), this.TINT.b)]
            : []),
          {
            label: "Naplaćeno",
            data: m.map((x) => Math.round(x.naplaceno)),
            backgroundColor: "#2f6fed",
            borderRadius: 3,
            maxBarThickness: this.compare.otkup ? 12 : 22,
          },
          ...(this.compare.otkup
            ? [this.cmpBar("Naplaćeno", this.alignCmp(m, cmp, (x) => Math.round(x.naplaceno)), this.TINT.a)]
            : []),
        ],
      };
    },
    otkupRows() {
      return (this.otkup.po_obavezi || []).map((r) => ({
        ...r,
        naziv: this.cleanName(this.getLabel("obaveza", r.obaveza)) || "—",
        razlika: (r.naplaceno || 0) - (r.ulozeno || 0),
      }));
    },
    otkupPageRows() {
      const from = (this.otkupPage - 1) * this.otkupPageSize;
      return this.otkupRows.slice(from, from + this.otkupPageSize);
    },
    // Every case behind the figures above, so a number can be traced to the
    // predmeti that produced it.
    caseRows() {
      const rows = (this.state.report.raw || []).map((r) => {
        const f = r.finansije || {};
        const c = f.computed || {};
        const flags = r.flags || {};
        return {
          id: r.id,
          broj: r.broj,
          datum_prijema: r.datum_prijema || "",
          datum_zatvaranja: r.datum_zatvaranja || "",
          datum: r.datum_prijema ? this.dateTimeFormat(r.datum_prijema) : "-",
          zatvoreno: r.datum_zatvaranja ? this.dateTimeFormat(r.datum_zatvaranja) : "-",
          status: parseInt(r.status, 10),
          vrsta: this.cleanName(this.getLabel("vrsta_osiguranja", r.vrsta_osiguranja)),
          uposleni: this.getLabel("users", r.owner),
          nosioc: (this.nosioci.find((n) => n.id === r.nosioc) || {}).naziv || "—",
          naplaceno: c.naplaceno || 0,
          placeno: c.placeno || 0,
          potrazivanje: c.potrazivanje || 0,
          profit: c.profit || 0,
          otkup_ponuden: !!flags.otkupljeno_ponuda,
          otkup_ponuda: f.otkup_ponuda || 0,
          otkup_prihvacen: !!flags.otkupljeno,
          otkup_placeno: f.otkup_placeno || 0,
        };
      });
      const { field, dir } = this.caseSort;
      const sign = dir === "asc" ? 1 : -1;
      return rows.sort((a, b) => {
        const x = a[field] === null || a[field] === undefined ? "" : a[field];
        const y = b[field] === null || b[field] === undefined ? "" : b[field];
        if (x === y) return 0;
        // blanks (an open case has no closing date) always sort last
        if (x === "") return 1;
        if (y === "") return -1;
        return x > y ? sign : -sign;
      });
    },
    casePageRows() {
      const from = (this.casePage - 1) * this.casePageSize;
      return this.caseRows.slice(from, from + this.casePageSize);
    },
    typeShare() {
      const rows = (this.state.report.summary.type.total || [])
        .map((c) => ({
          label: this.cleanName(this.getLabel("vrsta_osiguranja", parseInt(c.ids.vrsta_osiguranja, 10))),
          value: c.count,
        }))
        .sort((a, b) => b.value - a.value);
      const total = rows.reduce((a, r) => a + r.value, 0) || 1;
      return rows.map((r) => ({ ...r, pct: (r.value / total) * 100 }));
    },
    // Derived client-side from raw[] — the API has no per-obaveza breakdown.
    byObaveza() {
      const tally = (raw) => {
        const t = {};
        (raw || []).forEach((r) => {
          (r.obaveza || []).forEach((id) => {
            t[id] = (t[id] || 0) + 1;
          });
        });
        return t;
      };
      const current = tally(this.state.report.raw);
      const rows = Object.keys(current)
        .map((id) => ({ id: parseInt(id, 10), label: this.getLabel("obaveza", parseInt(id, 10)), value: current[id] }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 10);
      const datasets = [
        {
          label: "Odabrani period",
          data: rows.map((r) => r.value),
          backgroundColor: this.SERIES.a,
          borderRadius: 3,
          maxBarThickness: 20,
        },
      ];
      if (this.compare.obaveza) {
        // the same insurers, so the two periods are read side by side
        const prev = tally(this.compareReport && this.compareReport.raw);
        datasets.push({
          label: "Prethodni period",
          data: rows.map((r) => prev[r.id] || 0),
          backgroundColor: this.TINT.neutral,
          borderRadius: 3,
          maxBarThickness: 20,
        });
      }
      return { labels: rows.map((r) => r.label), datasets };
    },
    },
  methods: {
    getStatusLabel,
    // "prije"/"poslije" hold the cohort's activity that falls outside the
    // window — a case opened in December is often paid the following year.
    bucketLabel(key) {
      if (key === "prije") return "Prije perioda";
      if (key === "poslije") return "Nakon perioda";
      return moment(key, "YYYY-MM").format("MMM YY");
    },
    // The two periods can differ in whether they have edge buckets, so the
    // comparison is matched slot by slot rather than by raw index.
    alignCmp(current, previous, field) {
      const slot = (rows) => {
        const map = {};
        let i = 0;
        (rows || []).forEach((b) => {
          map[b.month === "prije" || b.month === "poslije" ? b.month : "m" + i++] = b;
        });
        return map;
      };
      const prev = slot(previous);
      let i = 0;
      return (current || []).map((b) => {
        const key = b.month === "prije" || b.month === "poslije" ? b.month : "m" + i++;
        const hit = prev[key];
        return hit ? field(hit) : null;
      });
    },
    // The previous period is drawn the same way as the series it mirrors —
    // bars against bars, lines against lines — so the two can be read as one
    // pair rather than as two different kinds of measurement.
    cmpBar(label, data, color) {
      return {
        label: label + " (prethodni period)",
        data,
        backgroundColor: color,
        borderRadius: 3,
        maxBarThickness: 12,
      };
    },
    cmpLine(label, data, color) {
      return {
        type: "line",
        label: label + " (prethodni period)",
        data,
        borderColor: color,
        backgroundColor: color,
        borderWidth: 2,
        borderDash: [5, 4],
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.25,
      };
    },
    sortCases(field) {
      if (this.caseSort.field === field) {
        this.caseSort = { field, dir: this.caseSort.dir === "asc" ? "desc" : "asc" };
      } else {
        this.caseSort = { field, dir: "asc" };
      }
      this.casePage = 1;
    },
    sortArrow(field) {
      if (this.caseSort.field !== field) return "⇅";
      return this.caseSort.dir === "asc" ? "↑" : "↓";
    },
    async toggleCompare(key) {
      this.compare[key] = !this.compare[key];
      if (this.compare[key]) await this.ensureCompare();
    },
    // One request serves every chart's comparison, and is reused until the
    // selected period changes.
    async ensureCompare() {
      const r = this.prevRange;
      if (!r) return;
      const sig = r.start + ".." + r.end;
      if (this.compareReport && this.compareSig === sig) return;
      this.compareLoading = true;
      try {
        const res = await API.Generic.create("report", this.buildRequest(r.start, r.end));
        this.compareReport = res.data.data;
        this.compareSig = sig;
      } catch (err) {
        console.log(err);
        this.compareReport = null;
        this.compareSig = null;
        this.$notify({ title: "Greška", message: "Prethodni period nije učitan.", type: "error" });
      }
      this.compareLoading = false;
    },
    // reference names are stored with an internal numeric prefix ("1 - AO")
    cleanName(v) {
      return String(v || "").replace(/^\s*\d+\s*-\s*/, "");
    },
    fmtInt(v) {
      return String(Math.round(Number(v) || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    fmtMoney(v) {
      const n = Number(v) || 0;
      const parts = n.toFixed(2).split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      return parts.join(",");
    },
    cell(col, key) {
      const [group, sub] = col.key.split(".");
      const node = ((this.fin || {})[group] || {})[sub] || {};
      if (key === "count_lost") return node.count_lost === undefined ? null : node.count_lost;
      return node[key] || 0;
    },
    applyPreset(p) {
      this.activePreset = p.key;
      const f = "YYYY-M-D";
      const now = moment();
      if (p.key === "today") {
        this.formData.start = now.format(f);
        this.formData.end = now.format(f);
      } else if (p.key === "month") {
        this.formData.start = now.clone().startOf("month").format(f);
        this.formData.end = now.format(f);
      } else if (p.key === "ytd") {
        this.formData.start = now.clone().startOf("year").format(f);
        this.formData.end = now.format(f);
      } else if (p.key === "last12") {
        this.formData.start = now.clone().subtract(11, "months").startOf("month").format(f);
        this.formData.end = now.format(f);
      } else if (p.key === "prev") {
        this.formData.start = now.clone().subtract(1, "year").startOf("year").format(f);
        this.formData.end = now.clone().subtract(1, "year").endOf("year").format(f);
      } else if (p.key === "q") {
        this.formData.start = now.clone().startOf("quarter").format(f);
        this.formData.end = now.format(f);
      } else if (p.key === "all") {
        this.formData.start = "2019-1-1";
        this.formData.end = now.format(f);
      }
      this.generateReport();
    },

    formatValue: function (nStr) {
      nStr += "";
      var x = nStr.split(".");
      var x1 = x[0];
      var x2 = x.length > 1 ? "." + x[1] : "";
      var rgx = /(\d+)(\d{3})/;
      while (rgx.test(x1)) {
        x1 = x1.replace(rgx, "$1" + "," + "$2");
      }
      return x1 + x2;
    },
    dateTimeFormat: function (date) {
      if (date == undefined) {
        return "";
      }
      return moment(date).format("DD/MM/YYYY HH:mm");
    },
    getLabel: function (field, id) {
      let res = "N/A";
      this.formContent[field].forEach((o) => {
        if (o.value == id) {
          res = o.label;
          return;
        }
      });
      return res;
    },
    generateReport: async function () {
      this.state.loading = true;

      let req = this.buildRequest(this.formData.start, this.formData.end);
      console.log(JSON.stringify(req));
      await API.Generic.create("report", req)
        .then((data) => {
          let d = data.data.data;
          this.state.report = d;
          this.otkupPage = 1;
          this.casePage = 1;
          // the previous period moves with the selected one
          this.compareReport = null;
          this.compareSig = null;
          if (this.compareOn) this.ensureCompare();
        })
        .catch((err) => {
          console.log(err);
          this.$notify({
            title: "Greška",
            message: err,
            type: "error",
          });
        });
      this.state.loading = false;
    },
    buildRequest(start, end) {
      let req = {
        start: start,
        end: end,
        obaveza: this.formData.obaveza,
        otkup_ponuda: !!this.formData.otkup_ponuda,
        nosioc: this.formData.nosioc || 0,
        owner: 0,
        vrsta_osiguranja: 0,
        saradnik: 0,
        status: -1,
        closed: this.formData.closed,
      };

      if (
        typeof this.formData.uposleni != "undefined" &&
        this.formData.uposleni != ""
      ) {
        req.owner = this.formData.uposleni;
      }
      if (
        typeof this.formData.vrsta_osiguranja != "undefined" &&
        this.formData.vrsta_osiguranja != ""
      ) {
        req.vrsta_osiguranja = this.formData.vrsta_osiguranja;
      }
      if (
        typeof this.formData.saradnik != "undefined" &&
        this.formData.saradnik != ""
      ) {
        req.saradnik = this.formData.saradnik;
      }
      if (
        typeof this.formData.status != "undefined" &&
        this.formData.status != ""
      ) {
        req.status = this.formData.status;
      }
      return req;
    },
    loadVO: async function () {
      return API.Generic.all("vrsta_osiguranja")
        .then((data) => {
          let d = data.data.data;
          // this.formData.vrsta_osiguranja = 0;
          // this.formContent.vrsta_osiguranja = [
          //   { value: 0, label: "Sve vrste" },
          // ];
          d.results.forEach((o) => {
            this.formContent.vrsta_osiguranja.push({
              value: o.id,
              label: o.naziv,
            });
          });
        })
        .catch((err) => {
          console.log(err);
        });
    },
    loadSaradnik: async function () {
      return API.Generic.all("saradnik", { active: { op: "eq", val: true } })
        .then((data) => {
          let d = data.data.data;
          console.log(d);
          // this.formData.saradnik = 0;
          // this.formContent.saradnik = [{ value: 0, label: "Svi saradnici" }];
          d.results.forEach((o) => {
            this.formContent.saradnik.push({
              value: o.id,
              label: o.ime + " " + o.prezime,
            });
          });
        })
        .catch((err) => {
          console.log(err);
        });
    },
    loadObaveza: async function () {
      return API.Generic.all("obaveza")
        .then((data) => {
          let d = data.data.data;
          this.formData.obaveza = [];
          this.formContent.obaveza = [];
          d.results.forEach((o) => {
            this.formContent.obaveza.push({
              value: o.id,
              label: o.naziv,
            });
          });
        })
        .catch((err) => {
          console.log(err);
        });
    },
    // Built from the one phase list so a renamed or added phase cannot drift
    // here. Unlike the case list this keeps Zatvoreno: reports can look at
    // closed cases.
    loadStatus: function () {
      this.formContent["status"] = [{ value: -1, label: "Faza - Sve" }].concat(
        selectablePhases().map((s) => ({ value: s.id, label: s.label }))
      );
    },
    loadSpecialFilter: function () {
      // test something
      // this.formContent.special_filter = [
      //   {
      //     value: 0,
      //     label: "Bez filtera",
      //   },
      // ];
    },
    loadUposelni: async function () {
      return API.Generic.all("user")
        .then((data) => {
          let d = data.data.data;
          console.log(d);
          this.formContent.users = [];
          d.results.forEach((o) => {
            this.formContent.users.push({
              value: o.id,
              label: o.first_name + " " + o.last_name,
            });
          });
        })
        .catch((err) => {
          console.log(err);
        });
    },
    load: function () {
      this.loadVO();
      this.loadSaradnik();
      this.loadObaveza();
      this.loadStatus();
      this.loadSpecialFilter();
      this.loadUposelni();
    },
  },
  created() {
    this.load();
    loadNosioci()
      .then((list) => (this.nosioci = list))
      .catch((e) => console.log(e));
    // Open on a useful period rather than an empty page waiting for Submit.
    this.applyPreset({ key: "today" });
  },
};
</script>

<style lang="scss" scoped>
.rep {
  flex-grow: 1;
  min-height: 0;
  overflow-y: auto;
}

.rep__inner {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-bottom: 24px;
}

.rep__title {
  font-size: var(--fs-title);
  font-weight: 700;
  color: var(--ink);
  margin: 0;
}

.rep__sub {
  font-size: var(--fs-sm);
  color: var(--ink-3);
  margin-top: 4px;
}

.card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  padding: 16px 18px;
}

.card__head {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 14px;
}

.card__title {
  font-size: var(--fs-lg);
  font-weight: 600;
  color: var(--ink);
  margin: 0;
}

.card__hint {
  font-size: var(--fs-sm);
  color: var(--ink-4);
}

.presets {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.preset {
  padding: 5px 12px;
  border: 1px solid var(--line-strong);
  border-radius: 999px;
  background: var(--surface);
  font: inherit;
  font-size: var(--fs-sm);
  color: var(--ink-2);
  cursor: pointer;

  &:hover {
    background: var(--surface-alt);
  }

  &.is-active {
    background: var(--navy);
    border-color: var(--navy);
    color: #fff;
    font-weight: 500;
  }
}

.filters-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;

  label {
    font-size: var(--fs-xs);
    color: var(--ink-4);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  ::v-deep .el-select,
  ::v-deep .el-date-editor {
    width: 168px;
  }
}

.field--check {
  padding-bottom: 5px;
}

.kpis {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
}

.kpi {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.kpi--accent {
  border-color: var(--brand-line);
  background: var(--brand-tint);
}

.kpi__label {
  font-size: var(--fs-xs);
  color: var(--ink-4);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.kpi__value {
  font-size: 22px;
  font-weight: 700;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
  line-height: 1.15;
}

.kpi__value--neg {
  color: var(--danger);
}

.kpi__meta {
  font-size: var(--fs-xs);
  color: var(--ink-3);
}

.grid2 {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.otkup-kpis {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
}

.okpi {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 12px 14px;
  border: 1px solid var(--line);
  border-radius: var(--r-md);
  min-width: 0;
}

.okpi--accent {
  background: var(--brand-tint);
  border-color: var(--brand-line);
}

.okpi__label {
  font-size: var(--fs-xs);
  color: var(--ink-4);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.okpi__value {
  font-size: 19px;
  font-weight: 700;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

.okpi__value--neg {
  color: var(--danger);
}

.okpi__meta {
  font-size: var(--fs-xs);
  color: var(--ink-3);
}

.ptbl-wrap {
  overflow-x: auto;
}

.ptbl {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--fs-sm);
  white-space: nowrap;

  th,
  td {
    padding: 8px 10px;
    border-bottom: 1px solid var(--line-soft);
    text-align: left;
  }

  thead th {
    position: sticky;
    top: 0;
    background: var(--surface-alt);
    color: var(--ink-3);
    font-weight: 600;
    font-size: var(--fs-xs);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  tbody tr:hover {
    background: var(--surface-alt);
  }
}

.ptbl__group {
  text-align: center !important;
  border-bottom: 1px solid var(--line) !important;
}

.ptbl th.ptbl__num,
.ptbl td.ptbl__num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.ptbl__sort {
  cursor: pointer;
  user-select: none;

  &:hover {
    color: var(--brand-ink);
  }
}

.ptbl__arrow {
  color: var(--ink-4);
  font-size: var(--fs-xs);
}

.ptbl__broj {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.ptbl__eye {
  width: 36px;
}

.ptbl__open {
  color: var(--ink-3);
  cursor: pointer;
  font-size: var(--fs-lg);

  &:hover {
    color: var(--brand);
  }
}

.ptbl__yes {
  color: var(--ok, #2f855a);
  font-weight: 600;
}

.ptbl__no {
  color: var(--danger);
  font-weight: 600;
}

.ptbl__neg {
  color: var(--danger);
}

.otbl__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 12px;
}

.otbl__count {
  font-size: var(--fs-sm);
  color: var(--ink-3);
}

.otbl-wrap {
  overflow-x: auto;
}

.otbl {
  width: 100%;
  // keeps the figures next to the names instead of stranded across a
  // full-width card
  max-width: 980px;
  border-collapse: collapse;
  font-size: var(--fs-md);
  table-layout: auto;

  th,
  td {
    padding: 8px 14px;
    white-space: nowrap;
  }

  thead th {
    font-size: var(--fs-xs);
    font-weight: 600;
    color: var(--ink-4);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    text-align: left;
    border-bottom: 1px solid var(--line);
  }

  tbody tr + tr td {
    border-top: 1px solid var(--line-soft);
  }

  // header and cell must share the same alignment or nothing lines up
  th.otbl__num,
  td.otbl__num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  // the name column absorbs the slack so the figures stay grouped together
  th.otbl__name,
  td.otbl__name {
    width: 100%;
    max-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.otbl__prinos {
  font-weight: 700;
}

.otbl__neg {
  color: var(--danger);
}

.fin-table-wrap {
  overflow-x: auto;
}

.fin-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--fs-md);

  th,
  td {
    padding: 8px 12px;
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  thead th {
    font-size: var(--fs-xs);
    font-weight: 600;
    color: var(--ink-4);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-bottom: 1px solid var(--line);
  }

  tbody th[scope="row"] {
    text-align: left;
    font-weight: 500;
    color: var(--ink-2);
  }

  tbody tr + tr th,
  tbody tr + tr td {
    border-top: 1px solid var(--line-soft);
  }

  tbody tr:last-child td,
  tbody tr:last-child th {
    font-weight: 700;
    color: var(--ink);
  }
}

.share {
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.share__row {
  display: grid;
  grid-template-columns: 150px minmax(0, 1fr) 54px 52px;
  align-items: center;
  gap: 12px;
  font-size: var(--fs-md);
}

.share__label {
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.share__track {
  height: 8px;
  background: var(--line-soft);
  border-radius: 4px;
  overflow: hidden;
}

.share__fill {
  display: block;
  height: 100%;
  background: var(--brand);
  border-radius: 4px;
}

.share__count,
.share__pct {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.share__count {
  font-weight: 600;
  color: var(--ink);
}

.share__pct {
  color: var(--ink-3);
  font-size: var(--fs-sm);
}

.share__empty {
  font-size: var(--fs-md);
  color: var(--ink-4);
}

.fin-table__sub {
  color: var(--ink-4);
  font-size: var(--fs-xs);
  margin-left: 4px;
}
</style>
